/**
 * V175 — Searchable multi-select dropdown (chip + checkbox pattern).
 *
 * Shares the Tailwind look of SearchableSelect but supports an array of
 * selected values. Built fresh so SearchableSelect's single-select API
 * (used across the makeover form, admin panels, invoice editor) stays
 * byte-for-byte unchanged.
 *
 * Props:
 *   • options:  Array<{ value: string, label: string, keywords?: string }>
 *   • values:   string[]  (controlled array of selected values)
 *   • onChange: (values: string[]) => void
 *   • placeholder, disabled, data-testid, ariaLabel, className
 *   • emptyText: shown when no filter match (default "No results")
 *
 * Behaviour (per V175 spec):
 *   • Click option → toggles it; dropdown stays open so user can pick more.
 *   • Searching does NOT clear existing selections.
 *   • Selected chips render in the trigger; X on a chip removes it without
 *     opening the dropdown. "Clear all" link clears every selection.
 *   • Checkbox-style indicator on every option row.
 *   • Keyboard: ArrowUp/Down navigate, Enter toggles, Esc closes, Backspace
 *     on an empty search box removes the last-selected chip.
 *   • Touch-friendly tap targets, aria-roles for screen readers.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X, Search, Check } from "lucide-react";

export default function SearchableMultiSelect({
    options,
    values = [],
    onChange,
    placeholder = "Select one or more…",
    disabled = false,
    className = "",
    "data-testid": testId,
    ariaLabel,
    emptyText = "No results",
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);
    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const selectedSet = useMemo(() => new Set(values), [values]);
    const selectedOptions = useMemo(
        () => values.map((v) => options.find((o) => o.value === v)).filter(Boolean),
        [values, options]
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => {
            const hay = `${o.label} ${o.keywords || ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [query, options]);

    useEffect(() => { setActiveIdx(0); }, [query, open]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    // Autofocus search input when opening
    useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 10); }, [open]);

    const toggle = (val) => {
        if (!onChange) return;
        if (selectedSet.has(val)) onChange(values.filter((v) => v !== val));
        else onChange([...values, val]);
    };

    const removeOne = (val, e) => {
        e?.stopPropagation();
        onChange?.(values.filter((v) => v !== val));
    };

    const clearAll = (e) => {
        e?.stopPropagation();
        onChange?.([]);
        setQuery("");
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(0, i - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const o = filtered[activeIdx];
            if (o) toggle(o.value);
        } else if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
        } else if (e.key === "Backspace" && !query && values.length > 0) {
            // Remove the last chip when the search box is empty.
            onChange?.(values.slice(0, -1));
        } else if (e.key === "Home") {
            e.preventDefault(); setActiveIdx(0);
        } else if (e.key === "End") {
            e.preventDefault(); setActiveIdx(filtered.length - 1);
        }
    };

    useEffect(() => {
        if (!open || !listRef.current) return;
        const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
        if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
    }, [activeIdx, open]);

    const hasSelection = values.length > 0;

    return (
        <div ref={rootRef} className={`relative ${className}`} data-testid={testId}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((v) => !v)}
                className={`w-full min-h-[38px] flex items-center gap-1.5 flex-wrap text-left px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none bg-white ${disabled ? "bg-slate-50 text-slate-400 cursor-not-allowed" : "cursor-pointer"} ${open ? "border-rose-500 ring-1 ring-rose-500" : ""}`}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                data-testid={testId ? `${testId}-trigger` : undefined}
            >
                {hasSelection ? (
                    <>
                        {selectedOptions.map((o) => (
                            <span
                                key={o.value}
                                className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 text-[12px] font-semibold px-2 py-0.5 rounded-full border border-rose-200"
                                data-testid={testId ? `${testId}-chip-${o.value}` : undefined}
                            >
                                <span className="truncate max-w-[180px]">{o.label}</span>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => removeOne(o.value, e)}
                                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && removeOne(o.value, e)}
                                    className="hover:bg-rose-100 rounded-full p-0.5 -mr-0.5 cursor-pointer"
                                    aria-label={`Remove ${o.label}`}
                                    data-testid={testId ? `${testId}-chip-remove-${o.value}` : undefined}
                                >
                                    <X className="w-3 h-3" />
                                </span>
                            </span>
                        ))}
                        <span className="flex-1" />
                    </>
                ) : (
                    <span className="text-slate-400 px-1">{placeholder}</span>
                )}
                {hasSelection && (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={clearAll}
                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && clearAll(e)}
                        className="text-[11px] text-slate-500 hover:text-rose-600 px-1 cursor-pointer"
                        data-testid={testId ? `${testId}-clear-all` : undefined}
                    >
                        Clear
                    </span>
                )}
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && !disabled && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
                    <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100">
                        <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Search…"
                            className="flex-1 text-sm bg-transparent outline-none"
                            data-testid={testId ? `${testId}-search` : undefined}
                        />
                        {query && (
                            <button type="button" onClick={() => setQuery("")} className="text-slate-400 hover:text-slate-700">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    <ul ref={listRef} role="listbox" className="overflow-y-auto flex-1" aria-multiselectable="true">
                        {filtered.length === 0 && (
                            <li className="px-3 py-3 text-sm text-slate-400 text-center">{emptyText}</li>
                        )}
                        {filtered.map((o, i) => {
                            const checked = selectedSet.has(o.value);
                            const active = i === activeIdx;
                            return (
                                <li
                                    key={o.value}
                                    data-idx={i}
                                    role="option"
                                    aria-selected={checked}
                                    onMouseEnter={() => setActiveIdx(i)}
                                    onClick={() => toggle(o.value)}
                                    data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                                    className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${active ? "bg-rose-50 text-rose-900" : "text-slate-700"} ${checked ? "font-semibold" : ""}`}
                                >
                                    <span className={`w-4 h-4 shrink-0 border rounded flex items-center justify-center ${checked ? "bg-rose-600 border-rose-600 text-white" : "border-slate-300 bg-white"}`}>
                                        {checked && <Check className="w-3 h-3" strokeWidth={3} />}
                                    </span>
                                    <span className="truncate">{o.label}</span>
                                </li>
                            );
                        })}
                    </ul>
                    {hasSelection && (
                        <div className="px-3 py-1.5 text-[11px] text-slate-500 border-t border-slate-100 bg-slate-50">
                            {values.length} selected
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
